import { Controller } from '@hotwired/stimulus'
import GLightbox from 'glightbox'

/**
 * Stimulus-контроллер галереи изображений на GLightbox.
 *
 * Собирает элементы галереи из `item` targets, открывает лайтбокс по клику,
 * добавляет горизонтальную ленту миниатюр и синхронизирует активную миниатюру
 * с текущим слайдом. При открытом лайтбоксе компенсирует исчезновение
 * скроллбара у `body` и шапки сайта.
 *
 * @extends {Controller}
 */
export default class GalleryController extends Controller {
  /**
   * Ссылки на изображения, которые открываются в лайтбоксе.
   *
   * @type {string[]}
   */
  static targets = ['item']

  /**
   * Активный инстанс GLightbox.
   *
   * @type {ReturnType<typeof GLightbox> | null}
   */
  #instance = null

  /**
   * Шапка сайта, для которой добавляется компенсационный `padding-right`.
   *
   * @type {HTMLElement | null}
   */
  #header = null

  /**
   * Ширина системного скроллбара в пикселях.
   *
   * @type {number | null}
   */
  #scrollbarWidth = null

  /**
   * Контейнер с миниатюрами слайдов.
   *
   * @type {HTMLDivElement | null}
   */
  #thumbsContainer = null

  /**
   * Инициализирует GLightbox, клики по элементам галереи и обработчики
   * событий открытия, закрытия и смены слайдов.
   */
  connect() {
    this.#header = document.querySelector('header')
    this.#scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth

    // eslint-disable-next-line new-cap
    this.#instance = GLightbox({
      elements: this.itemTargets.map((item) => ({
        href: item.href,
        type: 'image',
        title: item.dataset.title || '',
        description: item.dataset.text || ''
      })),

      loop: true,
      zoomable: false,
      draggable: false,
      openEffect: 'fade',
      closeEffect: 'fade',
      slideEffect: 'fade',
      preload: false,
      svg: {
        next: '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" data-icon="chevron-right"><use href="#icon-chevron-right"></use></svg>',
        prev: '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" data-icon="chevron-left"><use href="#icon-chevron-left"></use></svg>',
        close:
          '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" data-icon="cross"><use href="#icon-cross"></use></svg>'
      }
    })

    this.itemTargets.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault()
        this.#instance.openAt(this.itemTargets.indexOf(item))
      })
    })

    this.#instance.on('open', () => {
      document.body.style.paddingRight = `${this.#scrollbarWidth}px`

      if (this.#header) {
        this.#header.style.paddingRight = `${this.#scrollbarWidth}px`
      }

      const glightboxContainer = document.querySelector('.gcontainer')
      if (glightboxContainer) {
        const thumbnails = this.#thumbnails

        glightboxContainer.appendChild(thumbnails)
        this.#updateActiveThumbnail(this.#instance.index)

        thumbnails.classList.add('is-visible')
      }
    })

    this.#instance.on('close', () => {
      document.body.style.paddingRight = null
      if (this.#header) {
        this.#header.style.paddingRight = null
      }

      if (this.#thumbsContainer) {
        this.#thumbsContainer.classList.remove('is-visible')
      }
    })

    this.#instance.on('slide_before_change', ({ current }) => {
      this.#updateActiveThumbnail(current.index)
    })
  }

  /**
   * Уничтожает инстанс GLightbox и сбрасывает созданный контейнер миниатюр.
   */
  disconnect() {
    if (this.#instance) {
      this.#instance.destroy()
      this.#instance = null
    }
    this.#thumbsContainer = null
  }

  /**
   * Помечает активную миниатюру и прокручивает ленту к текущему слайду.
   *
   * @param {number} index — индекс активного слайда
   */
  #updateActiveThumbnail(index) {
    if (!this.#thumbsContainer) return

    const thumbs = this.#thumbsContainer.querySelectorAll('.gthumb')
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index)
    })

    const activeThumb = thumbs[index]
    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      })
    }
  }

  /**
   * Возвращает контейнер миниатюр, создавая его при первом обращении.
   *
   * Каждая миниатюра открывает соответствующий слайд через GLightbox.
   *
   * @returns {HTMLDivElement}
   */
  get #thumbnails() {
    if (!this.#thumbsContainer) {
      this.#thumbsContainer = document.createElement('div')
      this.#thumbsContainer.classList.add('glightbox-thumbs')

      this.#instance.elements.forEach((element, index) => {
        const img = document.createElement('img')
        img.src = element.href
        img.alt = element.slideConfig?.title || ''
        img.width = 116
        img.height = 65

        const imgSpan = document.createElement('span')
        imgSpan.classList.add('gthumb')
        imgSpan.appendChild(img)

        imgSpan.addEventListener('click', (evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          this.#instance.goToSlide(index)
        })

        this.#thumbsContainer.appendChild(imgSpan)
      })
    }

    return this.#thumbsContainer
  }
}
